import { evaluateFormula } from "./evaluate-formula";
import { formatCalcNumber } from "./format-calc-number";

export interface CalcPillDisplay {
  /** Text to render inside the pill — the computed number, or `fallback`. */
  text: string;
  /** Tooltip content, e.g. `= {{a}} × {{b}}`. */
  tooltip: string;
  /** True when the formula itself is malformed (parse error / division by zero). */
  hasError: boolean;
}

/**
 * Computes what a calculation-variable pill should show: its live value
 * (resolved from the current data via `resolve`), or `fallback` when an
 * operand is missing/unset — mirrors evaluateFormula's contract that a
 * missing operand is NOT an error state, just "no value yet".
 *
 * Pure — no DOM, no store reads. Callers pass in `resolve` (typically
 * `(token) => Number(getVariableValue(currentData, token))`) so this stays
 * testable with a stub.
 */
export const computeCalcPillDisplay = (
  formula: string,
  resolve: (token: string) => number | undefined,
  fallback = "",
): CalcPillDisplay => {
  const tooltip = `= ${formula}`;
  const { value, error } = evaluateFormula(formula, resolve);

  if (value === null) {
    return { text: fallback, tooltip, hasError: Boolean(error) };
  }

  return { text: formatCalcNumber(value), tooltip, hasError: false };
};
